import type { Message } from '../../api/types'
import { emojiLabel } from '../../lib/emoji'
import { userName } from '../../lib/channels'
import { useConversation } from './ConversationContext'
import { toggleReaction } from './messageActions'
import { ReactionBar } from './ReactionBar'

interface Props {
  message: Message
  canReact: boolean
  pickerOpen: boolean
  onClosePicker: () => void
  /** + in the quick picker: the emoji picker, opened under the button. */
  onMoreReactions: (button: HTMLElement) => void
}

export function Reactions({ message, canReact, pickerOpen, onClosePicker, onMoreReactions }: Props) {
  const { me, users } = useConversation()
  const reactions = message.reactions.filter((r) => r.userIds.length > 0)
  if (!reactions.length && !pickerOpen) return null

  return (
    // No swipe or hold starts on the reactions (spec §3.1).
    <div className="message-reactions" data-no-gestures>
      {pickerOpen ? (
        <ReactionBar
          message={message}
          className="popover reaction-picker"
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClosePicker()
          }}
          onReact={(emoji) => {
            onClosePicker()
            void toggleReaction(message, emoji, me.id)
          }}
          onMore={(button) => {
            onMoreReactions(button)
            onClosePicker()
          }}
        />
      ) : null}
      {reactions.length ? (
        <div className="reaction-chips" role="group" aria-label="Message reactions">
          {reactions.map((reaction) => {
            const selected = reaction.userIds.includes(me.id)
            const names = reaction.userIds.map((id) => (id === me.id ? 'You' : userName(users, id))).join(', ')
            const label = emojiLabel(reaction.emoji)
            return (
              <button
                key={reaction.emoji}
                type="button"
                className="reaction-chip"
                aria-pressed={selected}
                disabled={!canReact}
                title={names}
                aria-label={`${label}: ${reaction.userIds.length}. ${names}. ${selected ? 'Remove your reaction' : `React with ${label}`}`}
                onClick={() => void toggleReaction(message, reaction.emoji, me.id)}
              >
                <span className="reaction-emoji" aria-hidden="true">
                  {reaction.emoji}
                </span>
                <span className="reaction-count" aria-hidden="true">
                  {reaction.userIds.length}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
